import {createSlice, isAnyOf, type SliceSelectors} from '@reduxjs/toolkit'

import {selectCardPromotionGroup} from '@web/actions/cardPromotion'
import {getDeliveryAddressFee} from '@web/actions/deliveryInfo'
import {getPayLaterInfo, getPayLaterUserStatus, validatePayLaterDueDay} from '@web/actions/payLater'
import {selectFamilyPayPayMethod} from '@web/actions/paymentMethod'
import {
    changeDeliveryAreaExtraFee,
    changePayLaterPoint,
    resetPayLaterPoint,
    usingAllPayLaterPoint,
    enablePayLaterPoint,
    changeAlwaysUsesAllNaverPoint,
    enablePayLaterPointByAllPoint,
    updatePointPayment,
} from '@web/actions/pointPayment'
import {restoreSavedState, restoreState} from '@web/actions/restoreState'
import {PayLaterMemberStatus, PayLaterSignUpStatus} from '@web/core/constants'
import {
    resetPenaltyStatus,
    type PayLaterState,
    PayLaterInfoStatus,
    getInitialState,
    getPayLaterState,
} from '@web/slices/payLater/slice'

import {type PayLaterReducers, type MakeSliceOptions} from './interfaces'

export default (options?: MakeSliceOptions) =>
    createSlice<PayLaterState, PayLaterReducers, 'payLater', SliceSelectors<PayLaterState>>({
        name: 'payLater',
        initialState: getInitialState(),
        reducers: {
            updatePayDueValid(state, {payload}) {
                state.payDueValid = payload
            },
        },
        extraReducers: (builder) => {
            builder
                .addCase(getDeliveryAddressFee.fulfilled, (state, {payload}) => {
                    if (payload.changeDeliveryAreaExtraFeeMessage) {
                        state.enabled = false
                        state.usingAmount = 0
                    }
                })
                .addCase(getPayLaterUserStatus.fulfilled, (state, {payload}) => {
                    if (payload.isAlreadyRegistUser) {
                        state.signUpStatus = PayLaterSignUpStatus.SIGNED_UP
                    } else if (payload.isWithdrawalLocked) {
                        state.signUpStatus = PayLaterSignUpStatus.WITHDRAWAL_LOCK
                    } else {
                        state.signUpStatus = PayLaterSignUpStatus.AVAILABLE
                    }
                })
                .addCase(validatePayLaterDueDay.fulfilled, (state, {payload}) => {
                    state.payDueValid = !payload.shouldPayDueChange
                })
                /**
                 * C1, C2, 디컨 모두 항상 후불결제 API 조회
                 */
                .addCase(getPayLaterInfo.pending, (state) => {
                    state.status = PayLaterInfoStatus.INPROGRESS
                })
                .addCase(getPayLaterInfo.fulfilled, (state, {payload}) => {
                    const {info, tradeable} = payload
                    return getPayLaterState({
                        originState: state,
                        payLaterUserInfo: info,
                        payable: tradeable.payable,
                    })
                })
                .addCase(getPayLaterInfo.rejected, (state) => {
                    state.status = PayLaterInfoStatus.COMPLETED
                    state.memberStatus = PayLaterMemberStatus.MAINTENANCE
                })
            options?.extraReducers?.(builder)
            builder
                .addMatcher(
                    isAnyOf(
                        changeDeliveryAreaExtraFee.fulfilled,
                        changeAlwaysUsesAllNaverPoint.fulfilled,
                        changePayLaterPoint.fulfilled,
                        resetPayLaterPoint.fulfilled,
                        usingAllPayLaterPoint.fulfilled,
                        enablePayLaterPoint.fulfilled,
                        enablePayLaterPointByAllPoint.fulfilled,
                        updatePointPayment.fulfilled,
                    ),
                    (state, {payload}) => {
                        state.enabled = payload.enabledPayLater
                        state.usingAmount = payload.usingPayLaterPointAmount
                        state.useAll = payload.usePayLaterAll
                    },
                )
                .addMatcher(
                    isAnyOf(selectCardPromotionGroup.fulfilled, selectFamilyPayPayMethod.fulfilled),
                    (state) => {
                        state.enabled = false
                        state.usingAmount = 0
                    },
                )
                .addMatcher(isAnyOf(restoreSavedState.fulfilled, restoreState), (state, {payload}) => {
                    return payload.payLater ?? state
                })
                .addMatcher(isAnyOf(resetPenaltyStatus), (state) => {
                    if (state.overdue) {
                        state.overdue = {
                            amount: state.overdue.amount,
                            penaltyStatus: null,
                        }
                    }
                })
        },
    })
